import { useEffect, useState } from 'react';
import { KYC_DOC_TYPES, MAX_UPLOAD_BYTES, requiredKycKinds, type KycDocType, type KycKind, type KycState } from '@platform/shared';
import { cx, Spinner } from '../../components/ui';
import { api } from '../../lib/api';
import { Link } from '../../lib/router';
import { useLoad, useVersion } from '../../stores/account';
import { loadMe } from '../../stores/session';
import { AIcon, Alert, Card, Chip, errMsg, H2, Notice, PillButton, PillLink, Segmented, when } from './kit';

export const KIND: Record<KycKind, { label: string; hint: string }> = {
  id_front: { label: 'Front', hint: 'All four corners visible, no glare' },
  id_back: { label: 'Back', hint: 'Required for this document' },
  selfie: { label: 'Selfie with ID', hint: 'Your face and the document both readable' },
  poa: { label: 'Utility bill or bank statement', hint: 'Dated within the last 3 months, shows your name and address' },
};
const KIND_SHORT: Record<KycKind, string> = { id_front: 'ID (front)', id_back: 'ID (back)', selfie: 'Selfie with ID', poa: 'Proof of address' };
export const ACCEPT = 'image/jpeg,image/png,application/pdf';

export function Slot({ kind, label, state, onUploaded, disabled }: { kind: KycKind; label: string; state: KycState; onUploaded: (s: KycState) => void; disabled: boolean }) {
  const doc = state.documents.find((d) => d.kind === kind);
  const rejected = doc?.status === 'rejected';
  const ok = doc && !rejected;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const pick = async (f: File | undefined) => {
    if (!f) return;
    if (f.size > MAX_UPLOAD_BYTES) return setErr('That file is larger than 10 MB');
    setBusy(true);
    setErr(null);
    try {
      const fd = new FormData();
      fd.append('kind', kind);
      fd.append('file', f, f.name);
      onUploaded(await api.upload<KycState>('/api/kyc/documents', fd));
    } catch (x) {
      setErr(errMsg(x));
    } finally {
      setBusy(false);
    }
  };
  return (
    <label className={cx('flex min-w-0 items-center gap-3 rounded-[20px] border p-4 sm:gap-4 sm:p-5', rejected ? 'border-down' : 'border-dashed border-line-strong', disabled ? 'opacity-60' : 'cursor-pointer hover:border-fg')}>
      <span className={cx('grid size-12 shrink-0 place-items-center rounded-full', ok ? 'bg-up-bg text-up' : rejected ? 'bg-down-bg text-down' : 'bg-track')}>
        {busy ? <Spinner /> : <AIcon name={ok ? 'check' : rejected ? 'x' : 'upload'} />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[16px] md:text-[17px]">{label}</span>
        <span className={cx('block truncate text-[14px] md:text-[15px]', err || rejected ? 'text-down' : 'text-muted')}>
          {err ?? (rejected ? (doc.rejectReason ? `Declined: ${doc.rejectReason}. Upload a new one.` : 'Rejected. Upload a new one.') : doc ? `${doc.filename} · ${(doc.size / 1024 / 1024).toFixed(1)} MB` : KIND[kind].hint)}
        </span>
      </span>
      {!disabled && <span className="text-[15px] font-semibold">{doc ? 'Replace' : 'Upload'}</span>}
      <input type="file" accept={ACCEPT} className="hidden" disabled={disabled || busy} onChange={(e) => void pick(e.target.files?.[0])} />
    </label>
  );
}

const dayName = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString('en', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

/** Approved clients: the proof of address on file, and a way to send a newer one. */
function ProofOfAddress({ state, onState }: { state: KycState; onState: (s: KycState) => void }) {
  const p = state.poa;
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const lastUpdate = state.latest?.scope === 'poa_update' ? state.latest : null;
  const months = Math.round(p.maxAgeDays / 30);
  const pick = async (f: File | undefined) => {
    if (!f) return;
    if (f.size > MAX_UPLOAD_BYTES) return setMsg({ ok: false, text: 'That file is larger than 10 MB' });
    setBusy(true);
    setMsg(null);
    try {
      const fd = new FormData();
      fd.append('file', f, f.name);
      onState(await api.upload<KycState>('/api/kyc/poa', fd));
      setMsg({ ok: true, text: 'Thanks. We will review your new proof of address shortly.' });
    } catch (x) {
      setMsg({ ok: false, text: errMsg(x) });
    } finally {
      setBusy(false);
    }
  };
  const needsNew = p.status === 'update_required' || p.status === 'rejected' || p.stale;
  return (
    <Card className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <H2 className="!text-[22px]">Proof of address</H2>
        {p.status === 'pending' ? (
          <Chip tone="accent">Reviewing new document</Chip>
        ) : p.status === 'rejected' ? (
          <Chip tone="down">New document rejected</Chip>
        ) : needsNew ? (
          <Chip tone="warn">Update needed</Chip>
        ) : (
          <Chip tone="up">Up to date</Chip>
        )}
      </div>
      <p className="m-0 text-[16px] text-muted">
        {p.issuedOn ? `The document we have is dated ${dayName(p.issuedOn)}. ` : ''}
        It must be no more than {months} months old when you make a new deposit; when it is older, we ask for a new one before the deposit.
      </p>
      {p.status === 'rejected' && lastUpdate?.rejectReason && (
        <Alert>
          Your new proof of address was rejected: <b>“{lastUpdate.rejectReason}”</b> Please upload another one.
        </Alert>
      )}
      {p.status !== 'pending' && (
        <label className={cx('flex items-center gap-4 rounded-[20px] border border-dashed border-line-strong p-5', busy ? 'opacity-60' : 'cursor-pointer hover:border-fg')}>
          <span className="grid size-12 shrink-0 place-items-center rounded-full bg-track">{busy ? <Spinner /> : <AIcon name="upload" />}</span>
          <span className="min-w-0 flex-1">
            <span className="block text-[16px] md:text-[17px]">Upload a new proof of address</span>
            <span className="block text-[14px] text-muted md:text-[15px]">{KIND.poa.hint}</span>
          </span>
          <input type="file" accept={ACCEPT} className="hidden" disabled={busy} onChange={(e) => void pick(e.target.files?.[0])} />
        </label>
      )}
      <Notice msg={msg} onClose={() => setMsg(null)} />
    </Card>
  );
}

function Steps({ state }: { state: KycState }) {
  const s = state.status;
  const order = { none: 0, pending: 1, approved: 2, partial: 2, rejected: 2, expired: 3 }[s];
  const toSend = s === 'partial' ? state.needed.length : (state.latest?.rejectedKinds.length ?? 1);
  const items: [string, string][] = [
    ['Not submitted', ''],
    ['Pending', state.latest && s !== 'none' ? when(state.latest.createdAt) : ''],
    [s === 'rejected' ? 'Rejected' : s === 'partial' ? 'Partly verified' : 'Approved', state.latest?.reviewedAt ? when(state.latest.reviewedAt) : ''],
    [
      'Resubmit',
      s === 'expired' ? 'Upload a new ID' : (s === 'rejected' || s === 'partial') && toSend > 0 ? `Send ${toSend} document${toSend > 1 ? 's' : ''}` : '',
    ],
  ];
  return (
    <div className="mt-7 grid grid-cols-2 gap-4 lg:grid-cols-4">
      {items.map(([label, sub], i) => {
        const active = i === order;
        const done = i < order;
        const bad = active && s === 'rejected';
        const dim = i === 3 && s !== 'rejected' && s !== 'expired' && s !== 'partial';
        return (
          <div key={label} className={cx('flex flex-col gap-3 rounded-[16px] border bg-hero-card p-4', active ? 'border-white' : 'border-hero-line', dim && 'opacity-40')}>
            <span className={cx('grid size-9 place-items-center rounded-full', active ? (bad ? 'bg-[#f2557a] text-black' : 'bg-white text-black') : done ? 'bg-[#1fc7a0] text-black' : 'border-2 border-hero-line text-hero-muted')}>
              {done ? <AIcon name="check" size={16} /> : <b>{i + 1}</b>}
            </span>
            <b className="text-[16px]">{label}</b>
            <span className="num min-h-[18px] text-[13px] text-hero-muted">{(active || done) && sub ? sub : i === 3 ? sub : ''}</span>
          </div>
        );
      })}
    </div>
  );
}

export function KycTab() {
  const v = useVersion('kyc');
  const { data: fetched, error, setData } = useLoad<KycState>('/api/kyc', [v]);
  const [docType, setDocType] = useState<KycDocType>('passport');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const state = fetched;
  useEffect(() => {
    if (state?.latest) setDocType(state.latest.docType);
  }, [state?.latest]);

  if (error) return <Notice msg={{ ok: false, text: error }} />;
  if (!state) return <Spinner />;
  const s = state.status;
  const editable = s === 'none' || s === 'rejected' || s === 'expired';
  // Partly verified, or under review with a document declined: only those documents are sent again.
  const redo = [...new Set([...(s === 'partial' ? state.needed : []), ...state.documents.filter((d) => d.status === 'rejected').map((d) => d.kind)])];
  const redoSlots = (s === 'partial' || s === 'pending') && redo.length > 0;
  const freshForPartial = s === 'partial' && state.documents.some((d) => d.status === 'uploaded');
  const needed = requiredKycKinds(docType);
  const missing = needed.filter((k) => {
    const d = state.documents.find((x) => x.kind === k);
    return !d || d.status === 'rejected';
  });

  const submit = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.post<{ state: KycState }>('/api/kyc/submit', s === 'partial' ? {} : { docType });
      setData(r.state);
      setMsg({ ok: true, text: 'Documents submitted. We usually reply within 24 hours.' });
      void loadMe();
    } catch (x) {
      setMsg({ ok: false, text: errMsg(x) });
    } finally {
      setBusy(false);
    }
  };

  const heroText = {
    none: 'Upload three documents. Review usually takes under 24 hours.',
    pending: 'We are verifying your identity and will be in touch shortly.',
    approved: 'Your identity is verified. Deposits, trading, withdrawals and Yield are all available. Your name and date of birth are now locked.',
    rejected: 'One or more documents need to be replaced.',
    expired: 'Your ID document has expired. Upload a new ID and selfie to keep withdrawals open.',
    partial: 'Your identity is partly verified. Deposits and trading are open; send the documents below to enable withdrawals.',
  }[s];

  const submitted = state.latest ? state.documents.filter((d) => d.status !== 'uploaded') : [];

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-8 xl:grid-cols-[minmax(0,1fr)_minmax(0,500px)] 2xl:grid-cols-[minmax(0,1fr)_minmax(0,564px)]">
      <div className="flex min-w-0 flex-col gap-8">
        <section className="rounded-[24px] bg-hero p-5 text-hero-fg md:p-8">
          <h2 className="m-0 text-[22px] font-semibold md:text-[26px]">Identity verification</h2>
          <p className="m-0 mt-3 text-[16px] text-hero-muted md:text-[17px]">{heroText}</p>
          <Steps state={state} />
        </section>

        {editable && (
          <Card className="flex flex-col gap-8">
            {s === 'rejected' && state.latest?.rejectReason && (
              <Alert>
                Your submission was rejected{state.latest.reviewedAt ? ` on ${when(state.latest.reviewedAt, true)}` : ''}: <b>“{state.latest.rejectReason}”</b> Replace{' '}
                {state.latest.rejectedKinds.map((k) => KIND_SHORT[k]).join(', ')} and submit again. Your other documents are kept.
              </Alert>
            )}
            {state.missingProfile.length > 0 && (
              <Alert tone="info">
                Fill in your personal details first: {state.missingProfile.join(', ')}.{' '}
                <Link to="/account/personal" className="font-semibold text-fg">
                  Go to Details →
                </Link>
              </Alert>
            )}
            <div className="flex flex-col gap-4">
              <h3 className="m-0 text-[18px] font-semibold md:text-[20px]">1. Identity document</h3>
              <Segmented label="Document type" className="w-full sm:w-max max-sm:[&>button]:px-2 max-sm:[&>button]:text-[14px] max-sm:[&>button]:leading-tight max-sm:[&>button]:whitespace-normal" options={KYC_DOC_TYPES.map((d) => ({ id: d.id, label: d.label }))} value={docType} onChange={setDocType} />
              <div className={cx('grid gap-4', needed.includes('id_back') && 'md:grid-cols-2')}>
                <Slot kind="id_front" label={docType === 'passport' ? 'Passport · photo page' : `${KYC_DOC_TYPES.find((d) => d.id === docType)!.label} · ${KIND.id_front.label.toLowerCase()}`} state={state} onUploaded={setData} disabled={false} />
                {needed.includes('id_back') && <Slot kind="id_back" label={`${KYC_DOC_TYPES.find((d) => d.id === docType)!.label} · back`} state={state} onUploaded={setData} disabled={false} />}
              </div>
            </div>
            <div className="flex flex-col gap-4">
              <h3 className="m-0 text-[18px] font-semibold md:text-[20px]">2. Selfie holding your ID</h3>
              <div className="grid gap-4">
                <Slot kind="selfie" label={KIND.selfie.label} state={state} onUploaded={setData} disabled={false} />
              </div>
            </div>
            <div className="flex flex-col gap-4">
              <h3 className="m-0 text-[18px] font-semibold md:text-[20px]">3. Proof of address</h3>
              <div className="grid gap-4">
                <Slot kind="poa" label={KIND.poa.label} state={state} onUploaded={setData} disabled={false} />
              </div>
            </div>
            <p className="m-0 text-[15px] text-muted">JPG, PNG or PDF, up to 10 MB each.</p>
            <Notice msg={msg} onClose={() => setMsg(null)} />
            <PillButton size="lg" className="sm:w-max" busy={busy} disabled={missing.length > 0 || state.missingProfile.length > 0} onClick={() => void submit()}>
              {s === 'rejected' ? 'Resubmit for review' : s === 'expired' ? 'Submit new ID' : 'Submit for review'}
            </PillButton>
          </Card>
        )}

        {(redoSlots || freshForPartial) && (
          <Card className="flex flex-col gap-6">
            <H2 className="!text-[22px]">{s === 'partial' ? 'Documents we still need' : 'Please send this document again'}</H2>
            <p className="m-0 text-[16px] text-muted">
              {s === 'partial'
                ? 'Upload these, then send them for review. The documents we already accepted are kept.'
                : 'We could not accept it. The rest of your documents are still being reviewed; the new one joins them as soon as you upload it.'}
            </p>
            <div className="grid gap-4">
              {[...new Set([...redo, ...(s === 'partial' ? state.documents.filter((d) => d.status === 'uploaded').map((d) => d.kind) : [])])].map((k) => (
                <Slot key={k} kind={k} label={KIND_SHORT[k]} state={state} onUploaded={setData} disabled={false} />
              ))}
            </div>
            <p className="m-0 text-[15px] text-muted">JPG, PNG or PDF, up to 10 MB each.</p>
            {s === 'partial' && (
              <>
                <Notice msg={msg} onClose={() => setMsg(null)} />
                <PillButton size="lg" className="sm:w-max" busy={busy} disabled={redo.length > 0 || !freshForPartial} onClick={() => void submit()}>
                  Send for review
                </PillButton>
              </>
            )}
          </Card>
        )}

        {s === 'approved' && state.poa && <ProofOfAddress state={state} onState={setData} />}

        {!editable && (
          <section className="flex flex-col gap-4">
            <Notice msg={msg} />
            <h2 className="m-0 text-[24px] font-semibold md:text-[30px]">Submitted documents</h2>
            <table className="w-full border-collapse text-[15px] md:text-[17px]">
              <tbody>
                {submitted.map((d) => (
                  <tr key={d.id} className="border-b border-line">
                    <td className="px-2 py-5 md:px-6">{KIND_SHORT[d.kind]}</td>
                    <td className="truncate px-3 text-muted max-sm:hidden">{d.filename}</td>
                    <td className="px-2 text-right md:px-6">
                      {d.status === 'accepted' ? <Chip tone="up">Accepted</Chip> : d.status === 'rejected' ? <Chip tone="down">Declined</Chip> : <Chip tone="accent">Verifying</Chip>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </div>

      <Card className="flex flex-col gap-4">
        <H2 className="!text-[22px]">Why we verify</H2>
        <p className="m-0 text-[16px] leading-relaxed text-muted">Regulations require us to confirm who you are before you can withdraw. Review usually takes under 24 hours and we email you the result.</p>
        <ul className="m-0 flex list-none flex-col gap-3 p-0 text-[16px]">
          {['Trading and deposits stay open while we review', 'Your name and date of birth lock once you submit', 'Files are encrypted in transit and seen only by our compliance team'].map((t) => (
            <li key={t} className="flex gap-3">
              <AIcon name="check" className="shrink-0 text-up" />
              {t}
            </li>
          ))}
        </ul>
        {s === 'approved' && (
          <PillLink to="/account/withdraw" className="mt-2 w-max">
            Withdraw funds
          </PillLink>
        )}
      </Card>
    </div>
  );
}
